import { getShiftKey } from "@/constants/shifts";
import hasPlural from "./hasPlural";

const LAYOUT_CARDS = 0;
const LAYOUT_TABLE = 1;

/**
 * Narudžbe radnika po firmi: po jelu (ko je naručio jelo) ili po radniku (šta je naručio radnik).
 * Zahteva nasleđen ReportMealsTabMealEmployee (meals, report, selectedShifts, selectedLayout,
 * filters) i prop `shifts`.
 */
export default {
  mixins: [hasPlural],
  data: () => ({
    groupBy: "meal",
  }),
  computed: {
    isSingleShift() {
      return this.shifts.length === 1;
    },
    singleShiftLabel() {
      return this.$t("report_mobile.shift_n", { n: this.shifts[0] });
    },
    orderCompanies() {
      return this.meals
        .map((entry) => {
          const meals = entry.meals
            .map((meal) => this.toOrderMeal(entry.id, meal))
            .filter((meal) => meal.people.length);
          return {
            id: entry.id,
            name: this.toLatin(entry.company?.name || ""),
            meals,
            workers: this.groupByWorker(meals),
            allWorkers: this.countAllWorkers(entry.id),
          };
        })
        .filter((company) => company.meals.length);
    },
  },
  watch: {
    groupBy: {
      handler(groupBy) {
        // Štampa prati izabrani prikaz: po jelu → kartice, po radniku → tabela.
        this.selectedLayout = groupBy === "meal" ? LAYOUT_CARDS : LAYOUT_TABLE;
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * `people`: svi koji su naručili jelo (ista osoba u dve smene ima dva reda, svaki sa svojom smenom).
     * `shifts`: isti ljudi razvrstani po smeni, za prikaz grupa "Smena 1 … Smena 2 …".
     */
    toOrderMeal(companyId, meal) {
      const people = [];
      const shifts = [];
      this.selectedShifts.forEach((shiftId) => {
        const group = (meal[getShiftKey(shiftId)] || []).map((user) => ({
          key: `${shiftId}-${user.id}`,
          id: user.id,
          name: this.fullName(user),
          initials: this.initials(user),
          quantity: user.quantity || 1,
          shiftId,
        }));
        group.sort((a, b) => a.name.localeCompare(b.name));
        if (group.length) {
          shifts.push({
            id: shiftId,
            key: `${companyId}-${meal.id}-${shiftId}`,
            people: group,
            count: group.reduce((sum, person) => sum + person.quantity, 0),
          });
        }
        people.push(...group);
      });
      people.sort((a, b) => a.name.localeCompare(b.name));
      return {
        key: `${companyId}-${meal.id}`,
        name: meal.name,
        count: meal.total,
        people,
        shifts,
      };
    },
    groupByWorker(meals) {
      const workers = {};
      meals.forEach((meal) => {
        meal.people.forEach((person) => {
          if (!workers[person.id]) {
            workers[person.id] = {
              id: person.id,
              name: person.name,
              initials: person.initials,
              items: [],
            };
          }
          workers[person.id].items.push({
            key: `${meal.key}-${person.shiftId}`,
            meal: meal.name,
            quantity: person.quantity,
            shiftId: person.shiftId,
          });
        });
      });
      // Stavke radnika idu po smeni, pa po jelu: "Sm. 1 … , Sm. 3 …".
      Object.values(workers).forEach((worker) => {
        worker.items.sort((a, b) => a.shiftId - b.shiftId || a.meal.localeCompare(b.meal));
      });
      return Object.values(workers).sort((a, b) => a.name.localeCompare(b.name));
    },
    /** Svi radnici firme koji imaju narudžbu u izabranim smenama, bez obzira na pretragu. */
    countAllWorkers(companyId) {
      const ids = new Set();
      (this.report?.[companyId]?.meals || [])
        .filter((meal) => meal.order_id)
        .forEach((meal) => {
          this.selectedShifts.forEach((shiftId) => {
            const users = meal.users?.[`${shiftId}-smena`];
            (Array.isArray(users) ? users : []).forEach((user) => ids.add(user.id));
          });
        });
      return ids.size;
    },
    workersCountText(company) {
      const shown = company.workers.length;
      if (this.filters.search && company.allWorkers > shown) {
        return this.$t("report_mobile.workers_of", { n: shown, total: company.allWorkers });
      }
      return this.pluralT("report_mobile.workers_n", shown);
    },
    fullName(user) {
      return [user.name, user.lastname].filter(Boolean).map(this.toLatin).join(" ");
    },
    initials(user) {
      return [user.name, user.lastname]
        .filter(Boolean)
        .map((part) => this.toLatin(part).charAt(0))
        .join("");
    },
  },
};
